import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Globe, Check, Building2 } from 'lucide-react';
import { getDatabase, ref, get, set } from 'firebase/database';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { getCustomerType, setCustomerType } from '../../api/customerTypeApi';
import { CustomerType, CUSTOMER_TYPES, CUSTOMER_TYPE_LABELS } from '../../types/customerType';
import BackendLevelCard from './BackendLevelCard';
import EnabledBuildingTypesCard from './EnabledBuildingTypesCard';
import { recordActivity } from '../../api/activityApi';
import SuperadminBreadcrumb from './SuperadminBreadcrumb';

const InstanceSettingsPage: React.FC = () => {
    useDocumentTitle('Instansinställningar');

    const [currentUrl, setCurrentUrl] = useState<string>('');
    const [newUrl, setNewUrl] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const [currentType, setCurrentType] = useState<CustomerType | null>(null);
    const [newType, setNewType] = useState<CustomerType | ''>('');
    const [typeSubmitting, setTypeSubmitting] = useState(false);
    const [typeError, setTypeError] = useState<string | null>(null);
    const [typeSuccess, setTypeSuccess] = useState(false);

    const db = getDatabase();

    useEffect(() => {
        getCustomerType().then(type => {
            setCurrentType(type);
            setNewType(type ?? '');
        });
    }, []);

    const handleTypeSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setTypeError(null);
        setTypeSuccess(false);

        if (!newType) {
            setTypeError('Välj en kundtyp');
            return;
        }

        setTypeSubmitting(true);
        try {
            await setCustomerType(newType);
            setCurrentType(newType);
            setTypeSuccess(true);
            setTimeout(() => setTypeSuccess(false), 3000);
        } catch (err) {
            setTypeError(err instanceof Error ? err.message : 'Kunde inte spara kundtypen');
        } finally {
            setTypeSubmitting(false);
        }
    };

    // Load current value on mount
    useEffect(() => {
        get(ref(db, 'global/webAppUrl')).then(snapshot => {
            if (snapshot.exists() && snapshot.val()) {
                setCurrentUrl(snapshot.val());
                setNewUrl(snapshot.val());
            }
            setLoading(false);
        }).catch(err => {
            console.error('Error loading webAppUrl:', err);
            setLoading(false);
        });
    }, [db]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);

        const trimmedUrl = newUrl.trim();

        if (!trimmedUrl) {
            setError('URL krävs');
            return;
        }

        if (!trimmedUrl.startsWith('https://')) {
            setError('URL måste börja med https://');
            return;
        }

        // Validate URL format
        try {
            new URL(trimmedUrl);
        } catch {
            setError('Ogiltig URL-format');
            return;
        }

        // Normalize: remove trailing slashes
        const normalizedUrl = trimmedUrl.replace(/\/+$/, '');

        setSubmitting(true);

        try {
            await set(ref(db, 'global/webAppUrl'), normalizedUrl);
            recordActivity('settings.updated');
            setCurrentUrl(normalizedUrl);
            setNewUrl(normalizedUrl);
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            console.error('Error setting webAppUrl:', err);
            setError('Kunde inte spara inställningen');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <>
            <SuperadminBreadcrumb title="Instansinställningar" />
            <Container className="py-4">
                <div className="text-center">
                    <Spinner animation="border" />
                </div>
            </Container>
            </>
        );
    }

    return (
        <>
        <SuperadminBreadcrumb title="Instansinställningar" />
        <Container className="py-4" style={{ maxWidth: 600 }}>
            <h1 className="page-title mb-4">Instansinställningar</h1>

            <BackendLevelCard />

            <Card className="mb-4">
                <Card.Header className="d-flex align-items-center gap-2">
                    <Building2 size={18} />
                    Kundtyp
                </Card.Header>
                <Card.Body className="p-4">
                    <p className="text-muted mb-3">
                        Styr vilka fält som visas i apparna. Öppningsperiod visas bara för
                        stiftelser, kulturvärdesnivå döljs för bostadsrättsföreningar och
                        avskrivningar döljs för stiftelser. Apparna läser värdet vid start,
                        så en ändring slår igenom efter omstart.
                    </p>

                    {typeError && (
                        <Alert variant="danger" dismissible onClose={() => setTypeError(null)}>
                            {typeError}
                        </Alert>
                    )}

                    {typeSuccess && (
                        <Alert variant="success" className="d-flex align-items-center gap-2">
                            <Check size={18} />
                            Inställningen har sparats
                        </Alert>
                    )}

                    <Form onSubmit={handleTypeSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Kundtyp</Form.Label>
                            <Form.Select
                                value={newType}
                                onChange={(e) => setNewType(e.target.value as CustomerType | '')}
                                disabled={typeSubmitting}
                            >
                                <option value="">Ej vald</option>
                                {CUSTOMER_TYPES.map(type => (
                                    <option key={type} value={type}>{CUSTOMER_TYPE_LABELS[type]}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Button
                            type="submit"
                            variant="primary"
                            disabled={typeSubmitting || !newType || newType === currentType}
                        >
                            {typeSubmitting ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Sparar...
                                </>
                            ) : (
                                'Spara'
                            )}
                        </Button>
                    </Form>

                    {currentType && (
                        <div className="mt-4 pt-3 border-top">
                            <small className="text-muted">
                                Aktuell inställning: <code>{CUSTOMER_TYPE_LABELS[currentType]}</code>
                            </small>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <EnabledBuildingTypesCard />

            <Card>
                <Card.Header className="d-flex align-items-center gap-2">
                    <Globe size={18} />
                    Webb-URL
                </Card.Header>
                <Card.Body className="p-4">
                    <p className="text-muted mb-3">
                        URL:en används för att generera gästlänkar för externa användare.
                        Den ska peka på webbappen för denna instans.
                    </p>

                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert variant="success" className="d-flex align-items-center gap-2">
                            <Check size={18} />
                            Inställningen har sparats
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Webb-URL</Form.Label>
                            <Form.Control
                                type="url"
                                placeholder="https://app.molnfastigheter.se"
                                value={newUrl}
                                onChange={(e) => setNewUrl(e.target.value)}
                                disabled={submitting}
                            />
                            <Form.Text className="text-muted">
                                Exempel: https://app.molnfastigheter.se eller https://utveckling.molnfastigheter.se
                            </Form.Text>
                        </Form.Group>

                        <div className="d-flex gap-2">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={submitting || newUrl === currentUrl}
                            >
                                {submitting ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Sparar...
                                    </>
                                ) : (
                                    'Spara'
                                )}
                            </Button>
                        </div>
                    </Form>

                    {currentUrl && (
                        <div className="mt-4 pt-3 border-top">
                            <small className="text-muted">
                                Aktuell inställning: <code>{currentUrl}</code>
                            </small>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </Container>
        </>
    );
};

export default InstanceSettingsPage;
